import { UseFormMethods } from 'react-hook-form';
import { BeneficiaryDetailsData, BeneficiaryDetailsInputs, BeneficiaryDetailsProps } from './beneficiary-details';
import React, { useEffect, useState } from 'react';
import { StyledBeneficiaryCol } from 'beneficiaries/styles/beneficiary-details.styles';
import { Row, Col } from 'reactstrap';
import { InputField, InputFieldDisplayProperties } from 'rootstrap/components/forms/new-fields/input-field';
import { ValidationTypes } from 'rootstrap/components-old/root-schema-form/utils/validation';
import { getWording, BeneficiaryInputFields, isReadOnly } from 'site-config';
import { useSiteConfigContext } from 'style-context';
import { CountrySelectField } from 'rootstrap/components/forms/new-fields/extended-components/country-select-field';
import { CountryCode } from 'libphonenumber-js';
import { getDefaultCountryCode } from 'shared/utils';

interface BeneficiaryAddressFieldsProps extends BeneficiaryDetailsProps {
  form: UseFormMethods<Partial<BeneficiaryDetailsData>>;
}

export const BeneficiaryAddressFields = (props: BeneficiaryAddressFieldsProps) => {
  const { form, activeElement, beneficiary, setActiveElement } = props;
  const { siteConfig } = useSiteConfigContext();
  const [addressFieldsRequired, setAddressFieldsRequired] = useState(false);

  // Helper to determine if fields should be read-only
  const isBeneficiaryReadOnly = isReadOnly(props.isManagementContext, siteConfig, 'beneficiaries');

  // Helper function to get the correct input field config based on context
  const getBeneficiaryInputFieldConfig = (fieldName: keyof BeneficiaryInputFields) => {
    if (props.isManagementContext) {
      const managementConfig = siteConfig?.inputFields?.management?.beneficiaries?.[fieldName];
      return managementConfig || siteConfig?.inputFields?.beneficiaries?.[fieldName];
    }
    return siteConfig?.inputFields?.beneficiaries?.[fieldName];
  };

  const formValues = form.watch();
  const country = form.watch(BeneficiaryDetailsInputs.Country);

  useEffect(() => {
    if (
      formValues.addressLine1 ||
      formValues.addressLine2 ||
      formValues.suburb ||
      formValues.city ||
      formValues.areaCode
    ) {
      setAddressFieldsRequired(true);
    } else {
      setAddressFieldsRequired(false);
    }
  }, [formValues]);

  return (
    <>
      <StyledBeneficiaryCol sm={12}>
        <Row>
          <Col sm={12}>
            <InputField
              placeholder={undefined}
              defaultValue={beneficiary?.address?.line1}
              prefillValue={props.isManagementContext ? props.prefillValues.address?.addressLine1 : undefined}
              validators={
                addressFieldsRequired
                  ? [
                      {
                        validation: {
                          type: ValidationTypes.REQUIRED,
                        },
                      },
                    ]
                  : []
              }
              name={BeneficiaryDetailsInputs.AddressLine1}
              label={getWording({ wording: getBeneficiaryInputFieldConfig('addressLine1')?.label })}
              prefillAction={
                props.isManagementContext ? getBeneficiaryInputFieldConfig('addressLine1')?.prefillAction : undefined
              }
              form={form}
              {...steppedComponentsBehavior}
              isDisabled={isBeneficiaryReadOnly}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.AddressLine2,
                } as InputFieldDisplayProperties
              }
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              placeholder={undefined}
              defaultValue={beneficiary?.address?.line2}
              prefillValue={props.isManagementContext ? props.prefillValues.address?.addressLine2 : undefined}
              validators={[]}
              name={BeneficiaryDetailsInputs.AddressLine2}
              label={getWording({ wording: getBeneficiaryInputFieldConfig('addressLine2')?.label })}
              prefillAction={
                props.isManagementContext ? getBeneficiaryInputFieldConfig('addressLine2')?.prefillAction : undefined
              }
              form={form}
              {...steppedComponentsBehavior}
              isDisabled={isBeneficiaryReadOnly}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.Suburb,
                } as InputFieldDisplayProperties
              }
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              placeholder={undefined}
              defaultValue={beneficiary?.address?.suburb}
              prefillValue={props.isManagementContext ? props.prefillValues.address?.suburb : undefined}
              validators={
                addressFieldsRequired
                  ? [
                      {
                        validation: {
                          type: ValidationTypes.REQUIRED,
                        },
                      },
                    ]
                  : []
              }
              name={BeneficiaryDetailsInputs.Suburb}
              label={getWording({ wording: getBeneficiaryInputFieldConfig('suburb')?.label })}
              prefillAction={
                props.isManagementContext ? getBeneficiaryInputFieldConfig('suburb')?.prefillAction : undefined
              }
              form={form}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.City,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
              isDisabled={isBeneficiaryReadOnly}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              placeholder={undefined}
              defaultValue={beneficiary?.address?.city}
              prefillValue={props.isManagementContext ? props.prefillValues.address?.city : undefined}
              validators={
                addressFieldsRequired
                  ? [
                      {
                        validation: {
                          type: ValidationTypes.REQUIRED,
                        },
                      },
                    ]
                  : []
              }
              name={BeneficiaryDetailsInputs.City}
              label={getWording({ wording: getBeneficiaryInputFieldConfig('city')?.label })}
              prefillAction={
                props.isManagementContext ? getBeneficiaryInputFieldConfig('city')?.prefillAction : undefined
              }
              form={form}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.Country,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
              isDisabled={isBeneficiaryReadOnly}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <CountrySelectField
              clearable={true}
              placeholder={undefined}
              defaultValue={beneficiary?.address?.country || getDefaultCountryCode()}
              prefillValue={
                props.isManagementContext ? beneficiary?.address?.country || getDefaultCountryCode() : undefined
              }
              validators={
                addressFieldsRequired
                  ? [
                      {
                        validation: {
                          type: ValidationTypes.REQUIRED,
                        },
                      },
                    ]
                  : []
              }
              name={BeneficiaryDetailsInputs.Country}
              label={getWording({ wording: getBeneficiaryInputFieldConfig('country')?.label })}
              prefillAction={
                props.isManagementContext ? getBeneficiaryInputFieldConfig('country')?.prefillAction : undefined
              }
              form={form}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.AreaCode,
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
              isDisabled={isBeneficiaryReadOnly}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <InputField
              placeholder={undefined}
              defaultValue={beneficiary?.address?.areaCode}
              prefillValue={props.isManagementContext ? props.prefillValues.address?.areaCode : undefined}
              validators={[
                ...(addressFieldsRequired
                  ? [
                      {
                        validation: {
                          type: ValidationTypes.REQUIRED,
                        },
                      },
                    ]
                  : []),
                {
                  validation: {
                    type: ValidationTypes.AreaCode,
                    props: {
                      countryCode: (country as CountryCode) || 'ZA',
                    },
                  },
                },
              ]}
              name={BeneficiaryDetailsInputs.AreaCode}
              label={getWording({ wording: getBeneficiaryInputFieldConfig('areaCode')?.label })}
              prefillAction={
                props.isManagementContext ? getBeneficiaryInputFieldConfig('areaCode')?.prefillAction : undefined
              }
              form={form}
              isDisabled={isBeneficiaryReadOnly}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: '',
                } as InputFieldDisplayProperties
              }
              {...steppedComponentsBehavior}
            />
          </Col>
        </Row>
      </StyledBeneficiaryCol>
    </>
  );
};

const steppedComponentsBehavior = {
  isTouched: false,
  disableScrollToElement: false,
  disableActiveElement: false,
  hideBorder: true,
  disableNextButton: true,
  hideDivider: true,
};
